import classNames from "classnames";

import { PROJECT_MESSAGES } from "../../messages";
import type { FlatStep, ProjectPart, StepId } from "../../projectTypes";
import { isStepReachable } from "../../utils/project";

interface ProjectTimelineProps {
    parts: ProjectPart[];
    steps: FlatStep[];
    currentIndex: number;
    completedIds: Set<StepId>;
    onSelect: (index: number) => void;
}

// Steps grouped by part, with a line connecting them
const ProjectTimeline = ({
                             parts,
                             steps,
                             currentIndex,
                             completedIds,
                             onSelect
                         }: ProjectTimelineProps) => {
    return (
        <div className="border-t border-gray-100 pt-6">
            <h2 className="text-base font-bold mb-4">{PROJECT_MESSAGES.projectSteps}</h2>

            {parts.map((part) => {
                const partSteps = part.steps ?? [];

                return (
                    <div key={part.id} className="mb-6 last:mb-0">
                        <h3 className="text-sm font-semibold text-gray-500 mb-3">{part.name}</h3>

                        <div className="flex flex-col">
                            {partSteps.map((step, i) => {
                                const index = steps.findIndex((item) => item.id === step.id);
                                const completed = completedIds.has(step.id);
                                const active = index === currentIndex;
                                const disabled = !isStepReachable(index, currentIndex, steps, completedIds);
                                const isLast = i === partSteps.length - 1;

                                return (
                                    <button
                                        key={step.id}
                                        type="button"
                                        disabled={disabled}
                                        aria-current={active ? "step" : undefined}
                                        onClick={() => onSelect(index)}
                                        className="relative flex gap-3 pb-5 last:pb-0 text-start disabled:cursor-not-allowed"
                                    >
                                        {/* Line down to the next step */}
                                        {!isLast && (
                                            <div className="absolute start-[13px] top-7 bottom-0 w-px bg-gray-200" />
                                        )}

                                        <div
                                            className={classNames(
                                                "relative z-10 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-xs",
                                                completed
                                                    ? "border-green-500 bg-green-500 text-white"
                                                    : active
                                                        ? "border-black bg-black text-white"
                                                        : disabled
                                                            ? "border-gray-200 bg-gray-100 text-gray-300"
                                                            : "border-gray-300 bg-white text-gray-500"
                                            )}
                                        >
                                            {completed ? "✓" : i + 1}
                                        </div>

                                        <span
                                            className={classNames(
                                                "pt-1 text-sm",
                                                disabled
                                                    ? "text-gray-300"
                                                    : active
                                                        ? "font-semibold text-gray-900"
                                                        : "text-gray-600"
                                            )}
                                        >
                                            {PROJECT_MESSAGES.step} {i + 1}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default ProjectTimeline;